import { useState, useRef } from 'react';
import { Camera, Upload, X, Fuel, Gauge, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Badge from '../ui/Badge';
import { supabase } from '../../lib/supabase';
import { getOficinaId } from '../../lib/office';
import { onlyDigits } from '../../utils/masks';

const FUEL_LEVELS = [
  { value: 'empty', label: 'Reserva', color: 'bg-red-500' },
  { value: 'quarter', label: '1/4', color: 'bg-orange-500' },
  { value: 'half', label: '1/2', color: 'bg-yellow-500' },
  { value: 'three_quarters', label: '3/4', color: 'bg-lime-500' },
  { value: 'full', label: 'Cheio', color: 'bg-green-500' },
];

const DAMAGE_ZONES = [
  'Para-choque dianteiro',
  'Para-choque traseiro',
  'Capô',
  'Porta dianteira esquerda',
  'Porta dianteira direita',
  'Porta traseira esquerda',
  'Porta traseira direita',
  'Teto',
  'Roda dianteira esquerda',
  'Roda dianteira direita',
  'Roda traseira esquerda',
  'Roda traseira direita',
];

export default function CheckinForm({ osId, clienteId, veiculoId, onComplete, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [odometer, setOdometer] = useState('');
  const [fuelLevel, setFuelLevel] = useState('half');
  const [exteriorCondition, setExteriorCondition] = useState('');
  const [dashboardNotes, setDashboardNotes] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [damages, setDamages] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [damageNote, setDamageNote] = useState('');
  const fileInputRef = useRef(null);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setLoading(true);
    try {
      const oficinaId = await getOficinaId();
      if (!oficinaId) throw new Error('Oficina não encontrada');

      const uploadPromises = files.map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${oficinaId}/checkins/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('vehicle-media')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: signedUrlData } = await supabase.storage
          .from('vehicle-media')
          .createSignedUrl(filePath, 60 * 60);

        return {
          id: Date.now() + Math.random(),
          url: signedUrlData?.signedUrl || '',
          path: filePath,
          type: file.type.startsWith('image') ? 'image' : 'video',
          name: file.name,
        };
      });

      const uploadedPhotos = await Promise.all(uploadPromises);
      setPhotos((prev) => [...prev, ...uploadedPhotos]);
      toast.success(`${uploadedPhotos.length} foto(s) adicionada(s)`);
    } catch (error) {
      toast.error('Erro ao fazer upload: ' + error.message);
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (photoId) => {
    setPhotos((prev) => prev.filter((p) => p.id !== photoId));
  };

  const addDamage = () => {
    if (!selectedZone || !damageNote.trim()) {
      toast.error('Selecione uma zona e descreva o dano');
      return;
    }

    const newDamage = {
      id: Date.now(),
      zone: selectedZone,
      note: damageNote,
      photoId: null,
    };

    setDamages((prev) => [...prev, newDamage]);
    setSelectedZone(null);
    setDamageNote('');
    toast.success('Dano registrado');
  };

  const removeDamage = (damageId) => {
    setDamages((prev) => prev.filter((d) => d.id !== damageId));
  };

  const handleSubmit = async () => {
    if (!odometer.trim()) {
      toast.error('Informe a quilometragem');
      return;
    }

    setLoading(true);
    try {
      const oficinaId = await getOficinaId();
      if (!oficinaId) throw new Error('Oficina não encontrada');

      const { data: checkinId, error } = await supabase.rpc('create_vehicle_checkin', {
        p_oficina_id: oficinaId,
        p_os_id: osId,
        p_cliente_id: clienteId,
        p_veiculo_id: veiculoId,
        p_odometer_value: Number(odometer),
        p_fuel_level: fuelLevel,
        p_exterior_condition: exteriorCondition,
        p_dashboard_notes: dashboardNotes,
        p_customer_notes: customerNotes,
        p_photos: photos.map((p) => ({ url: p.url, path: p.path, type: p.type })),
        p_damages: damages.map((d) => ({ zone: d.zone, note: d.note })),
      });

      if (error) throw error;

      toast.success('Check-in realizado com sucesso!');
      onComplete?.({
        id: checkinId,
        os_id: osId,
        cliente_id: clienteId,
        veiculo_id: veiculoId,
        odometer_value: Number(odometer),
        fuel_level: fuelLevel,
        exterior_condition: exteriorCondition,
        dashboard_notes: dashboardNotes,
        customer_notes: customerNotes,
        status: 'completed',
        completed_at: new Date().toISOString(),
      });
    } catch (error) {
      toast.error('Erro ao realizar check-in: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Gauge size={20} />
            Quilometragem e Combustível
          </h3>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium">Quilometragem (km)</label>
            <Input
              mask="integer"
              value={odometer}
              onChange={(e) => setOdometer(onlyDigits(e.target.value, 7))}
              placeholder="Ex: 50000"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium flex items-center gap-2">
              <Fuel size={16} />
              Nível de combustível
            </label>
            <div className="flex gap-2">
              {FUEL_LEVELS.map((level) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => setFuelLevel(level.value)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                    fuelLevel === level.value
                      ? `${level.color} border-transparent text-white`
                      : 'border-white/10 bg-surface text-zinc-400 hover:text-zinc-200'
                  }`}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Camera size={20} />
            Fotos do Veículo
          </h3>
          <p className="text-sm text-zinc-400">Registre o estado atual do veículo</p>
        </div>

        <div className="mb-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handlePhotoUpload}
            className="hidden"
            id="photo-upload"
          />
          <label htmlFor="photo-upload">
            <Button as="span" disabled={loading}>
              <Upload size={16} className="mr-2" />
              Adicionar Fotos
            </Button>
          </label>
        </div>

        {photos.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img
                  src={photo.url}
                  alt={photo.name}
                  className="h-32 w-full rounded-lg object-cover border border-white/10"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(photo.id)}
                  className="absolute right-2 top-2 rounded-full bg-red-500 p-1.5 opacity-0 transition group-hover:opacity-100"
                >
                  <X size={14} className="text-white" />
                </button>
                {photo.type === 'video' && (
                  <Badge className="absolute left-2 top-2">Vídeo</Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle size={20} />
            Danos Existentes
          </h3>
          <p className="text-sm text-zinc-400">Documente avarias para evitar conflitos</p>
        </div>

        <div className="mb-4 grid gap-3 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium">Local do dano</label>
            <select
              value={selectedZone || ''}
              onChange={(e) => setSelectedZone(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm"
            >
              <option value="">Selecione...</option>
              {DAMAGE_ZONES.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Descrição</label>
            <Input
              value={damageNote}
              onChange={(e) => setDamageNote(e.target.value)}
              placeholder="Ex: Arranhão superficial"
            />
          </div>
        </div>

        <Button onClick={addDamage} disabled={!selectedZone || !damageNote.trim()}>
          <AlertTriangle size={16} className="mr-2" />
          Adicionar Dano
        </Button>

        {damages.length > 0 && (
          <div className="mt-4 space-y-2">
            {damages.map((damage) => (
              <div key={damage.id} className="flex items-start justify-between rounded-lg border border-white/10 bg-surface2 p-3">
                <div className="flex-1">
                  <p className="font-medium">{damage.zone}</p>
                  <p className="text-sm text-zinc-400">{damage.note}</p>
                </div>
                <button
                  type="button"
                  onClick={() => removeDamage(damage.id)}
                  className="ml-3 rounded-lg bg-red-500/10 p-2 text-red-400 hover:bg-red-500/20"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="mb-6">
          <h3 className="text-lg font-semibold">Observações</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">Condição externa geral</label>
            <Input
              value={exteriorCondition}
              onChange={(e) => setExteriorCondition(e.target.value)}
              placeholder="Ex: Pintura em bom estado, pequenos arranhões"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Observações do painel</label>
            <Input
              value={dashboardNotes}
              onChange={(e) => setDashboardNotes(e.target.value)}
              placeholder="Ex: Luz de check acionada, computador de bordo funcionando"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Observações do cliente</label>
            <Input
              value={customerNotes}
              onChange={(e) => setCustomerNotes(e.target.value)}
              placeholder="Ex: Cliente relata ruído ao frear"
            />
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="ghost" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Processando...' : 'Concluir Check-in'}
        </Button>
      </div>
    </div>
  );
}
